import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ru";

import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import ErrorHandler from "../../../components/common/ErrorHandler";
import {
    ErrorHandlerContext,
    ErrorHandlerContextProvider,
} from "../../../contexts/ErrorHandlerContext";
import useUser, { isUserDoctor, getAuthorized, postAuthorized, patchAuthorized, deleteAuthorized } from "../../../hooks/User";
import { ROUTES } from "../../../routes";
import {
    UsersIcon,
    PlusIcon,
    SearchIcon,
    EditIcon,
    TrashIcon,
    XIcon,
    ChevronRightIcon,
    DownloadIcon,
    FolderIcon,
} from "../../../components/icons";

import "./patient-groups.css";

dayjs.locale("ru");

const DEFAULT_PAGE_SIZE = 10;

function FolderIconLocal() {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/>
        </svg>
    );
}

function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText, isLoading, danger }) {
    if (!isOpen) return null;
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <TrashIcon size={32} />
                </div>
                <h3>{title}</h3>
                <p>{message}</p>
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={isLoading}>Отмена</button>
                    <button className={`btn-confirm ${danger ? "btn-danger" : ""}`} onClick={onConfirm} disabled={isLoading}>
                        {isLoading ? "Загрузка..." : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}

function GroupFormModal({ isOpen, onClose, onSubmit, initialValue = "", title, submitText, isLoading }) {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState("");

    useEffect(() => {
        if (isOpen) {
            setValue(initialValue);
            setError("");
        }
    }, [isOpen, initialValue]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const trimmed = value.trim();
        if (!trimmed) {
            setError("Введите название группы");
            return;
        }
        if (trimmed.length > 255) {
            setError("Название не должно превышать 255 символов");
            return;
        }
        onSubmit(trimmed);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-form" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button className="modal-close" onClick={onClose} disabled={isLoading}><XIcon /></button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-field">
                            <label htmlFor="group-name">Название группы</label>
                            <input
                                id="group-name"
                                type="text"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                placeholder="Например: Гипертония"
                                autoFocus
                                className={error ? "error" : ""}
                                disabled={isLoading}
                            />
                            {error && <span className="field-error">{error}</span>}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={isLoading}>Отмена</button>
                        <button type="submit" className="btn-primary" disabled={isLoading}>{isLoading ? "Сохранение..." : submitText}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Pagination({ currentPage, totalPages, onPageChange, totalItems, pageSize }) {
    if (totalPages <= 1) return null;
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    return (
        <div className="pagination">
            <div className="pagination-info">{startItem}-{endItem} из {totalItems}</div>
            <div className="pagination-controls">
                <button className="pagination-btn" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}><ChevronRightIcon style={{ transform: "rotate(180deg)" }} /></button>
                <span className="pagination-current">{currentPage} / {totalPages}</span>
                <button className="pagination-btn" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRightIcon /></button>
            </div>
        </div>
    );
}

function PatientGroupsContent() {
    const { loading: userLoading, error: userError, data: userData } = useUser();
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);

    const [groups, setGroups] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    const [formModal, setFormModal] = useState({ isOpen: false, group: null });
    const [formLoading, setFormLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState({ isOpen: false, group: null });
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        if (userLoading) return;
        if (userError) navigate(ROUTES.AUTH, { viewTransition: true });
        else if (!isUserDoctor(userData)) navigate(ROUTES.PROFILE, { viewTransition: true });
    }, [userLoading, userError, userData, navigate]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const loadGroups = useCallback(async () => {
        if (userLoading || !isUserDoctor(userData)) return;
        setIsLoading(true);
        try {
            const params = {
                st: (currentPage - 1) * DEFAULT_PAGE_SIZE,
                limit: DEFAULT_PAGE_SIZE,
            };
            const response = await getAuthorized("/doctor/patient_groups", { params });
            let list = response.data?.groups || [];
            const total = response.data?.total_count || 0;
            if (debouncedSearch.trim()) {
                const q = debouncedSearch.trim().toLowerCase();
                list = list.filter((g) => g.name?.toLowerCase().includes(q));
            }
            setGroups(list);
            setTotalCount(total);
        } catch (error) {
            console.error("Failed to load groups:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при загрузке групп"]);
            setGroups([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, debouncedSearch, userLoading, userData, setErrorList]);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const handleCreate = async (name) => {
        setFormLoading(true);
        try {
            await postAuthorized("/doctor/patient_groups", { name });
            setFormModal({ isOpen: false, group: null });
            loadGroups();
        } catch (error) {
            console.error("Failed to create group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при создании группы"]);
        } finally {
            setFormLoading(false);
        }
    };

    const handleRename = async (name) => {
        if (!formModal.group) return;
        setFormLoading(true);
        try {
            await patchAuthorized(`/doctor/patient_groups/${formModal.group.id}`, { name });
            setFormModal({ isOpen: false, group: null });
            loadGroups();
        } catch (error) {
            console.error("Failed to rename group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при переименовании группы"]);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmModal.group) return;
        setDeleteLoading(true);
        try {
            await deleteAuthorized(`/doctor/patient_groups/${confirmModal.group.id}`, {});
            setConfirmModal({ isOpen: false, group: null });
            loadGroups();
        } catch (error) {
            console.error("Failed to delete group:", error);
            setErrorList((prev) => [...prev, error.response?.data?.error_description || "Ошибка при удалении группы"]);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDownloadReport = useCallback(async (group, e) => {
        e.stopPropagation();
        try {
            const response = await getAuthorized("/doctor/patient_group_report", {
                params: { group_id: group.id },
                responseType: "blob",
            });
            const href = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = href;
            link.download = `Отчет по группе ${group.name}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } catch (error) {
            console.error("Failed to download group report:", error);
            setErrorList((prev) => [...prev, "Ошибка при скачивании отчёта по группе"]);
        }
    }, [setErrorList]);

    const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));

    if (userLoading) {
        return (
            <div className="patient-groups-page">
                <ProfileNavigation isDoctor={true} />
                <div className="patient-groups-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="patient-groups-page">
            <SEO title="Группы пациентов — Spiria" description="Управление группами пациентов" noindex={true} />
            <ProfileNavigation isDoctor={isUserDoctor(userData)} />
            <div className="patient-groups-container">
                <div className="patient-groups-header">
                    <div className="patient-groups-title">
                        <FolderIconLocal />
                        <div>
                            <h1>Группы пациентов</h1>
                            <p className="patient-groups-subtitle">Создавайте группы для удобного отслеживания и отчётности</p>
                        </div>
                    </div>
                    <button
                        className="btn-primary btn-create"
                        onClick={() => setFormModal({ isOpen: true, group: null })}
                    >
                        <PlusIcon size={18} />
                        <span>Создать группу</span>
                    </button>
                </div>

                <div className="patient-groups-toolbar">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по названию группы..."
                        />
                        {searchQuery && (
                            <button className="search-clear" onClick={() => setSearchQuery("")} title="Очистить поиск">×</button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="patient-groups-loading">
                        <div className="spinner"></div>
                        <p>Загрузка групп...</p>
                    </div>
                ) : groups.length === 0 ? (
                    <div className="empty-groups-state">
                        <div className="empty-icon"><FolderIcon size={48} /></div>
                        <h3>{debouncedSearch ? "Группы не найдены" : "Пока нет групп пациентов"}</h3>
                        <p>
                            {debouncedSearch
                                ? "Попробуйте изменить параметры поиска"
                                : "Создайте первую группу, чтобы объединить пациентов и формировать отчёты"}
                        </p>
                        {!debouncedSearch && (
                            <button className="btn-primary" onClick={() => setFormModal({ isOpen: true, group: null })}>
                                <PlusIcon size={18} />
                                <span>Создать группу</span>
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="patient-groups-stats">
                            <span>Всего групп: {totalCount}</span>
                        </div>
                        <div className="patient-groups-list">
                            {groups.map((group) => (
                                <div
                                    key={group.id}
                                    className="group-card"
                                    onClick={() => navigate(`${ROUTES.PATIENT_GROUP_DETAIL.replace(":groupId", group.id)}`)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            navigate(`${ROUTES.PATIENT_GROUP_DETAIL.replace(":groupId", group.id)}`);
                                        }
                                    }}
                                >
                                    <div className="group-card-main">
                                        <div className="group-icon"><UsersIcon size={22} /></div>
                                        <div className="group-info">
                                            <h3 className="group-name" title={group.name}>{group.name}</h3>
                                            <div className="group-meta">
                                                <span className="group-patients">{group.patient_count} {pluralizePatients(group.patient_count)}</span>
                                                <span className="group-date">С {dayjs(group.creation_dt).format("DD.MM.YYYY")}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="group-card-actions" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            className="btn-action btn-report"
                                            title="Скачать отчёт"
                                            onClick={(e) => handleDownloadReport(group, e)}
                                        >
                                            <DownloadIcon size={16} />
                                            <span>Отчёт</span>
                                        </button>
                                        <button
                                            className="btn-action btn-edit"
                                            title="Редактировать"
                                            onClick={() => setFormModal({ isOpen: true, group })}
                                        >
                                            <EditIcon size={16} />
                                            <span>Изменить</span>
                                        </button>
                                        <button
                                            className="btn-action btn-remove"
                                            title="Удалить"
                                            onClick={() => setConfirmModal({ isOpen: true, group })}
                                        >
                                            <TrashIcon size={16} />
                                            <span>Удалить</span>
                                        </button>
                                        <div className="group-arrow"><ChevronRightIcon size={18} /></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            totalItems={totalCount}
                            pageSize={DEFAULT_PAGE_SIZE}
                        />
                    </>
                )}
            </div>

            <GroupFormModal
                isOpen={formModal.isOpen}
                onClose={() => setFormModal({ isOpen: false, group: null })}
                onSubmit={formModal.group ? handleRename : handleCreate}
                initialValue={formModal.group?.name || ""}
                title={formModal.group ? "Переименовать группу" : "Создать группу"}
                submitText={formModal.group ? "Сохранить" : "Создать"}
                isLoading={formLoading}
            />

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal({ isOpen: false, group: null })}
                onConfirm={handleDelete}
                title="Удалить группу?"
                message={`Вы уверены, что хотите удалить группу «${confirmModal.group?.name}»? Пациенты не будут удалены.`}
                confirmText="Удалить"
                isLoading={deleteLoading}
                danger
            />
        </div>
    );
}

function pluralizePatients(count) {
    if (count % 10 === 1 && count % 100 !== 11) return "пациент";
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return "пациента";
    return "пациентов";
}

export default function PatientGroupsPage() {
    return (
        <ErrorHandlerContextProvider>
            <PatientGroupsContent />
            <ErrorHandler />
        </ErrorHandlerContextProvider>
    );
}
